import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, Divider, FormControlLabel, Grid, Paper, Switch,
  TextField, Typography, CircularProgress, Tooltip, IconButton, Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon, CloudDownload as CloudDownloadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import i18n, { intlLocale } from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';

const ZIP_TAX_BASE = '/api/v1/content/admin/zip-tax-rates';

// Prefilled with the validated Avalara-derived dataset (percent-based Rate% column). All
// fields are editable so a different source can be pointed at without a code change.
const DEFAULT_IMPORT = {
  sourceUrl: 'https://raw.githubusercontent.com/MirzaAreebBaig/Woocommerce-US-ZipCodes-TaxRates/main/tax_rates.csv',
  colZip: 'Postcode / ZIP',
  colState: 'State code',
  colCombinedRate: 'Rate %',
  rateIsPercent: true,
  minExpectedRows: 30000,
};

const asPercent = (fraction) =>
  fraction == null ? '—' : `${(Number(fraction) * 100).toFixed(3)}%`;

// For the data-loading callbacks, so a language switch doesn't refetch the status.
const tTax = (key, options) => i18n.t(key, { ns: 'taxRates', ...options });

const ZipTaxRatesPanel = () => {
  const { t } = useTranslation('taxRates');
  const { getAccessToken } = useAuth();

  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const [lookupZip, setLookupZip] = useState('');
  const [lookupResult, setLookupResult] = useState(null); // { found, data } | { found:false }
  const [lookingUp, setLookingUp] = useState(false);

  const [form, setForm] = useState(DEFAULT_IMPORT);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error(tTax('errors.authRequired'));
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessToken]);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}${ZIP_TAX_BASE}/status`, { headers });
      if (!res.ok) throw new Error(tTax('errors.fetchZipStatus'));
      setStatus(await res.json());
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingStatus(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleLookup = async () => {
    const zip = lookupZip.trim();
    if (!zip) return;
    setLookingUp(true);
    setLookupResult(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}${ZIP_TAX_BASE}/${encodeURIComponent(zip)}`, { headers });
      if (res.status === 404) {
        setLookupResult({ found: false });
      } else if (res.status === 400) {
        toast.error(t('errors.invalidZip'));
      } else if (!res.ok) {
        throw new Error(t('errors.lookupFailed'));
      } else {
        setLookupResult({ found: true, data: await res.json() });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLookingUp(false);
    }
  };

  const handleImport = async () => {
    if (!form.sourceUrl.trim()) { toast.error(t('errors.sourceUrlRequired')); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}${ZIP_TAX_BASE}/refresh`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sourceUrl: form.sourceUrl.trim(),
          colZip: form.colZip.trim() || null,
          colState: form.colState.trim() || null,
          colCombinedRate: form.colCombinedRate.trim() || null,
          rateIsPercent: form.rateIsPercent,
          minExpectedRows: Number(form.minExpectedRows) || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(translateServerMessage(data.message, t('errors.importFailed')));
      setImportResult(data);
      if (data.status === 'SUCCESS') {
        toast.success(t('toast.loaded', { value: data.rowsUpserted }));
      } else {
        toast.warn(t('toast.importStatus', { status: importStatusLabel(data.status), message: translateServerMessage(data.message) }));
      }
      fetchStatus();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setImporting(false);
    }
  };

  const setField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // The import status (SUCCESS, FAILED, SKIPPED) stays raw for the colour; only the text is translated.
  const importStatusLabel = (s) => (s ? t(`importStatus.${s}`, { defaultValue: s }) : s);
  const fmtNumber = (n) => Number(n).toLocaleString(intlLocale());

  const statusColor = (s) => (s === 'SUCCESS' ? 'success' : s === 'FAILED' ? 'error' : 'warning');
  const lastRun = status?.lastRun && status.lastRun.status ? status.lastRun : null;

  return (
    <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 3, mt: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('zip.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('zip.subtitle')}
          </Typography>
        </Box>
        <Tooltip title={t('zip.refreshStatus')}>
          <IconButton onClick={fetchStatus} color="primary"><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* Status */}
      <Box display="flex" gap={3} alignItems="center" flexWrap="wrap" my={2}>
        {loadingStatus ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <Box>
              <Typography variant="caption" color="text.secondary">{t('zip.zipCount')}</Typography>
              <Typography variant="h6" fontWeight={700}>
                {status?.zipCount != null ? fmtNumber(status.zipCount) : '—'}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">{t('zip.lastImport')}</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {lastRun ? (
                  <>
                    <Chip size="small" color={statusColor(lastRun.status)} label={importStatusLabel(lastRun.status)} />
                    <Typography variant="body2" color="text.secondary">
                      {lastRun.rowsUpserted != null ? t('zip.rows', { value: fmtNumber(lastRun.rowsUpserted) }) : ''}
                      {lastRun.startedAt ? ` · ${new Date(lastRun.startedAt).toLocaleString(intlLocale())}` : ''}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">{t('zip.never')}</Typography>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
      {lastRun?.message && lastRun.status !== 'SUCCESS' && (
        <Alert severity={statusColor(lastRun.status)} sx={{ mb: 2 }}>{translateServerMessage(lastRun.message)}</Alert>
      )}

      <Divider sx={{ my: 2 }} />

      {/* ZIP lookup */}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>{t('zip.lookupTitle')}</Typography>
      <Box display="flex" gap={1} alignItems="center" mb={1} flexWrap="wrap">
        <TextField
          size="small" label={t('zip.zipCode')} value={lookupZip}
          onChange={(e) => setLookupZip(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
          sx={{ width: 160 }}
        />
        <Button variant="outlined" startIcon={lookingUp ? <CircularProgress size={16} /> : <SearchIcon />}
          onClick={handleLookup} disabled={lookingUp}
          sx={{ borderRadius: 2, textTransform: 'none' }}>
          {t('zip.lookup')}
        </Button>
        {lookupResult && !lookupResult.found && (
          <Chip size="small" color="warning" label={t('zip.notFound')} />
        )}
        {lookupResult?.found && (
          <Typography variant="body2">
            <b>{lookupResult.data.zipCode}</b> ({lookupResult.data.stateCode}) →{' '}
            <b>{asPercent(lookupResult.data.combinedRate)}</b>
            {lookupResult.data.regionName ? ` · ${lookupResult.data.regionName}` : ''}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Import / refresh */}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>{t('import.title')}</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('import.description')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField fullWidth size="small" label={t('import.sourceUrl')}
            value={form.sourceUrl} onChange={setField('sourceUrl')} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label={t('import.zipColumn')}
            value={form.colZip} onChange={setField('colZip')} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label={t('import.stateColumn')}
            value={form.colState} onChange={setField('colState')} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label={t('import.rateColumn')}
            value={form.colCombinedRate} onChange={setField('colCombinedRate')} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" type="number" label={t('import.minRows')}
            value={form.minExpectedRows} onChange={setField('minExpectedRows')}
            helperText={t('import.minRowsHelp')} />
        </Grid>
        <Grid item xs={12} sm={4} display="flex" alignItems="center">
          <FormControlLabel
            control={<Switch checked={form.rateIsPercent} onChange={setField('rateIsPercent')} color="primary" />}
            label={t('import.rateIsPercent')}
          />
        </Grid>
      </Grid>

      <Box display="flex" alignItems="center" gap={2} mt={2}>
        <Button variant="contained"
          startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <CloudDownloadIcon />}
          onClick={handleImport} disabled={importing}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
          {importing ? t('import.loading') : t('import.load')}
        </Button>
        {importing && (
          <Typography variant="body2" color="text.secondary">
            {t('import.inProgress')}
          </Typography>
        )}
      </Box>

      {importResult && (
        <Alert severity={statusColor(importResult.status)} sx={{ mt: 2 }}>
          <b>{importStatusLabel(importResult.status)}</b>
          {importResult.rowsFetched != null && ` · ${t('import.fetched', { value: fmtNumber(importResult.rowsFetched) })}`}
          {importResult.rowsUpserted != null && ` · ${t('import.upserted', { value: fmtNumber(importResult.rowsUpserted) })}`}
          {importResult.rowsSkipped != null && ` · ${t('import.skipped', { value: fmtNumber(importResult.rowsSkipped) })}`}
          {importResult.message ? ` — ${translateServerMessage(importResult.message)}` : ''}
        </Alert>
      )}
    </Paper>
  );
};

export default ZipTaxRatesPanel;
